import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

export function useDebouncedValue<T>(value: T, delay = 1000): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed
      // within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay], // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

/**
 * It's the same as `useState`, but it debounces the output value
 */
export function useDebouncedState<T>(value: T, delay = 1000): [T, Dispatch<SetStateAction<T>>, T] {
  const [saved, setSaved] = useState<T>(value);

  const debounced = useDebouncedValue(saved, delay);

  return [debounced, setSaved, saved];
}

export const useDebouncedCallback = <T extends Array<unknown>>(
  callback: (...args: T) => void,
  delay = 1000,
): ((...args: T) => void) => {
  const timeoutRef = useRef<number | null>(null);
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    (...args: T): void => {
      const currentCallback = callbackRef.current;
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current);
      }

      const newTimeoutId = window.setTimeout(() => {
        currentCallback(...args);
        if (timeoutRef.current === newTimeoutId) {
          timeoutRef.current = null;
        }
      }, delay);

      timeoutRef.current = newTimeoutId;
    },
    [delay],
  );
};
